import { useEffect } from 'react';
import { useExtensionTheme } from '../hooks/useExtensionTheme';
import { applyIdentity } from '../lib/identity';

/**
 * Componente Provider de Tema para aiRecording
 * 
 * Este componente:
 * - Inicializa el hook useExtensionTheme
 * - Configura el listener de postMessage para comandos de tema desde ACV
 * - Aplica el tema automáticamente
 */
export function ExtensionThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme, isLoading, error } = useExtensionTheme();

  // Configurar listener de postMessage para comandos de tema y datos de usuario
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Obtener origins permitidos desde variable de entorno
      const allowedOrigins = (
        (window as any).REACT_APP_ACV_PARENT_APP || 
        (import.meta.env?.VITE_ACV_PARENT_APP as string) || 
        ''
      ).split(',').map((origin: string) => origin.trim()).filter(Boolean);

      // En desarrollo, también permitir localhost
      const isDevelopment = import.meta.env?.MODE === 'development' || import.meta.env?.DEV === true;
      const isLocalhost = event.origin.includes('localhost') || 
                          event.origin.includes('127.0.0.1');

      // Validar origin
      const isValidOrigin = 
        allowedOrigins.length === 0 ||
        allowedOrigins.includes(event.origin) ||
        (isDevelopment && isLocalhost);

      if (!isValidOrigin) {
        console.warn('[aiRecording] Message from unauthorized origin:', event.origin);
        return;
      }

      // Procesar datos de usuario desde ACV
      if (event.data?.message?.user && event.data?.message?.isFromACV) {
        const userData = event.data.message.user;
        console.log('[aiRecording] Received user data from ACV:', { 
          hasAccessToken: !!userData.accessToken,
          email: userData.email 
        });
        
        // Marcar que estamos embebidos desde ACV
        localStorage.setItem('isFromACV', 'true');
        
        // Same path as main.tsx: a different user clears the previous user's cache,
        // so this runs before anything user-specific is written below.
        if (userData.email) applyIdentity(userData.email, userData.slug);

        if (userData.accessToken) {
          localStorage.setItem('access_token', userData.accessToken);
        }

        if (userData.email) {
          try {
            const userObj = {
              mail: userData.email,
              email: userData.email,
              accessToken: userData.accessToken,
              role: userData.role
            };
            localStorage.setItem('user', JSON.stringify(userObj));
            localStorage.setItem('msg', JSON.stringify({
              user: userObj,
              isFromACV: true
            }));
          } catch (e) {
            console.warn('[aiRecording] Error saving user data:', e);
          }
        }
      }
      
      // También procesar si el mensaje tiene isFromACV directamente
      if (event.data?.isFromACV === true || event.data?.message?.isFromACV === true) {
        console.log('[aiRecording] Received isFromACV flag from ACV');
        localStorage.setItem('isFromACV', 'true');
        // Disparar evento para que useIsFromACV lo detecte
        window.dispatchEvent(new CustomEvent('acv-embed-detected'));
      }

      // Procesar comandos de tema
      if (event.data?.type === 'theme-change' || 
          event.data?.type === 'theme-reload' ||
          event.data?.type === 'APPLY_THEME') {
        // main.tsx ya re-emite estos comandos de forma temprana.
        // Aquí solo persistimos como fallback para no duplicar aplicación de tema.
        (window as any).__acvPendingThemeCommand = event.data;
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Limpiar temas antiguos del localStorage al montar
  useEffect(() => {
    try {
      // Eliminar preferencias de tema antiguo
      const oldPrefs = localStorage.getItem('airecording.preferences');
      if (oldPrefs) {
        const prefs = JSON.parse(oldPrefs);
        if (prefs.theme || prefs.accentColor) {
          delete prefs.theme;
          delete prefs.accentColor;
          localStorage.setItem('airecording.preferences', JSON.stringify(prefs));
          console.log('[ExtensionThemeProvider] ✅ Limpiadas preferencias de tema antiguo');
        }
      }
    } catch (e) {
      console.warn('[ExtensionThemeProvider] Error limpiando localStorage:', e);
    }
  }, []);

  if (error) {
    console.error('[ExtensionThemeProvider] Error loading theme:', error);
  }

  // No bloquear la UI mientras carga el tema - mostrar children inmediatamente
  // El tema se aplicará en background cuando esté listo
  return <>{children}</>;
}

